import { ErrorCode } from '@fluenceur/types';
import { isAxiosError } from 'axios';

/** Read the backend `code` from an axios error, if present. */
export const apiErrorCode = (error: unknown): string | undefined => {
  if (isAxiosError(error)) {
    return (error.response?.data as { code?: string } | undefined)?.code;
  }
  return undefined;
};

const KNOWN_ERROR_CODES = new Set<string>(Object.values(ErrorCode));

/**
 * The backend `code` narrowed to one this build knows about, or null when the
 * failure never reached the API (network, timeout) or carries a code shipped
 * after this build. Callers that branch on several codes take this rather than
 * {@link apiErrorCode}: an exhaustive switch over `ErrorCode` stays honest, and
 * an unknown code falls into the generic branch instead of a stale one.
 */
export const knownApiErrorCode = (error: unknown): ErrorCode | null => {
  const code = apiErrorCode(error);
  return code !== undefined && KNOWN_ERROR_CODES.has(code) ? (code as ErrorCode) : null;
};

/** Read the HTTP status from an axios error, if a response exists. */
export const apiErrorStatus = (error: unknown): number | undefined =>
  isAxiosError(error) ? error.response?.status : undefined;

/**
 * Whether an error carries a specific backend ErrorCode. Narrowing first is
 * redundant for a single code — an unknown string cannot equal an enum member
 * anyway — but it keeps one definition of "a code this build knows".
 */
export const isApiErrorCode = (error: unknown, code: ErrorCode): boolean =>
  knownApiErrorCode(error) === code;

/**
 * User-facing message from a backend error. The dashboard surfaces the
 * envelope's English `message` as-is (house rule); the caller supplies the
 * localized fallback for network-level failures without a body.
 */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  if (isAxiosError(error)) {
    const message = (error.response?.data as { message?: string } | undefined)?.message;
    if (message) return message;
  }
  return fallback;
};

/** One VineJS field failure as AdonisJS renders it on a 422. */
export interface ApiValidationFieldError {
  field: string;
  message: string;
}

interface VineValidationBody {
  errors?: Array<{ field?: unknown; message?: unknown }>;
}

/**
 * First field failure of a VineJS 422 (`{ errors: [{ field, message, rule }] }`).
 *
 * Returns null for any other error shape, so callers keep their generic
 * fallback. Only the first entry is surfaced: fixing it usually reveals or
 * resolves the rest, and a toast listing every field is unreadable.
 */
export const apiValidationFieldError = (error: unknown): ApiValidationFieldError | null => {
  if (!isAxiosError(error) || error.response?.status !== 422) return null;

  const entries = (error.response.data as VineValidationBody | undefined)?.errors;
  if (!Array.isArray(entries)) return null;

  for (const entry of entries) {
    if (typeof entry?.field === 'string' && entry.field && typeof entry.message === 'string') {
      return { field: entry.field, message: entry.message };
    }
  }
  return null;
};
